import React, { useState, useEffect, useMemo } from 'react';
import { AmountInput } from './AmountInput';
import {
  X,
  MessageSquare,
  Send,
  Copy,
  Check,
  ExternalLink,
  Sparkles,
  Edit3,
  Calendar,
  DollarSign,
  Scale,
  Clock,
  ShieldAlert,
  Phone,
  Building,
  User,
  Info,
  RefreshCw,
  Tag,
  Users,
} from 'lucide-react';
import { Account } from '../../types';
import { useSRMS } from '../../context/SRMSContext';
import { getLinkedAccounts, mergedOutstanding, otsTotalOf } from '../../utils/linkedAccounts';
import { productOf } from '../../utils/collections';

import { toLocalDateString } from '../../utils/dates';
export type WhatsAppCategory =
  | 'PAYMENT_REMINDER'
  | 'LOK_ADALAT'
  | 'OTS_OFFER'
  | 'BROKEN_PTP'
  | 'FIELD_VISIT';

export type WhatsAppLanguage = 'EN' | 'MR' | 'HI';

export interface WhatsAppDraftModalProps {
  account: Account;
  isOpen: boolean;
  onClose: () => void;
  defaultCategory?: WhatsAppCategory;
}

export const WhatsAppDraftModal: React.FC<WhatsAppDraftModalProps> = ({
  account,
  isOpen,
  onClose,
  defaultCategory = 'PAYMENT_REMINDER',
}) => {
  const { currentUser, addAgentNote, logWhatsAppOffer, accounts } = useSRMS();

  // Mode: Auto-Generated (Smart dynamic template) vs Manual (Free-form editor)
  const [draftMode, setDraftMode] = useState<'auto' | 'manual'>('auto');

  // Category & Language - Defaulting to Marathi ('MR') as the primary system language
  const [category, setCategory] = useState<WhatsAppCategory>(defaultCategory);
  const [language, setLanguage] = useState<WhatsAppLanguage>('MR');

  // Synchronize category and ensure default language is always Marathi ('MR') whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setCategory(defaultCategory);
      setLanguage('MR');
    }
  }, [isOpen, defaultCategory]);

  // Recipient: customer primary, alternate/guarantor, coordinator, agent, or custom mobile
  const [recipientType, setRecipientType] = useState<'customer' | 'alternate' | 'coordinator' | 'agent' | 'custom'>('customer');
  const [customMobile, setCustomMobile] = useState<string>('');

  // A customer holding several loans gets ONE message covering all of them.
  // This screen used to read a single account, so a borrower with three
  // loans received a demand for one of them and reasonably concluded that
  // was the whole debt -- and the settlement figure quoted was for that one
  // loan while the offer letter said "full and final".
  const linked = useMemo(
    () => getLinkedAccounts(account, accounts),
    [account, accounts]
  );
  const isMultiAccount = linked.length > 1;

  const totalOutstanding = useMemo(
    () => mergedOutstanding(linked),
    [linked]
  );

  const overdueAmount = useMemo(
    () => linked.reduce((sum, a) => sum + Math.max(0, a.overdueAmount || 0), 0),
    [linked]
  );

  /** "12345678 (Home Loan), 87654321 (Gold Loan)" */
  const allLoanNumbers = useMemo(
    () =>
      linked
        .map((a) => {
          const no = a.loanNumber || a.accountId || 'N/A';
          const fac = a.facility || productOf(a);
          return fac ? `${no} (${fac})` : no;
        })
        .join(', '),
    [linked]
  );

  /** A line per loan with its own balance, for the body of the message. */
  const perAccountBreakdown = useMemo(() => {
    if (!isMultiAccount) return '';
    return linked
      .map((a, i) => {
        const no = a.loanNumber || a.accountId || 'N/A';
        const bal = Math.max(
          0,
          a.originalOutstandingAmount || a.outstandingAmount || 0
        ).toLocaleString('en-IN');
        return `   ${i + 1}. ${no} — ₹${bal}/-`;
      })
      .join('\n');
  }, [linked, isMultiAccount]);

  // Customizable OTS parameters
  // Across all the customer's loans, not just the one that was open. A
  // settlement offered as "full and final" has to cover the full debt.
  const [otsAmount, setOtsAmount] = useState<number>(() => {
    const summed = linked.reduce((sum, a) => sum + otsTotalOf(a), 0);
    if (summed > 0) return summed;
    return Math.round(totalOutstanding * 0.5);
  });

  const [otsValidTill, setOtsValidTill] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 10);
    return toLocalDateString(d);
  });

  // Customizable Bank Name (defaults to Bank of Maharashtra or account.bankName)
  const [bankNameCustom, setBankNameCustom] = useState<string>(
    account.bankName || 'Bank of Maharashtra'
  );

  // Customizable Lok Adalat parameters (Defaults to 12 Sep 2026 Chhatrapati Sambhajinagar)
  const [lokAdalatDate, setLokAdalatDate] = useState<string>('2026-09-12');
  const [lokAdalatTime, setLokAdalatTime] = useState<string>('सकाळी 11:00 वाजता');
  const [lokAdalatVenue, setLokAdalatVenue] = useState<string>(
    'जिल्हा व सत्र न्यायालय परिसर, नवीन इमारत,\nछत्रपती संभाजीनगर – 431001, महाराष्ट्र.'
  );

  // Customizable Payment Reminder Due Date
  const [paymentDueDate, setPaymentDueDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return toLocalDateString(d);
  });

  // Payment Link / UPI ID. Deliberately blank: the old hard-coded address was
  // not a real payment destination, so each user now types their own.
  const [paymentLink, setPaymentLink] = useState<string>('');

  // The active message text currently in the editor
  const [messageText, setMessageText] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);
  const [logToNotes, setLogToNotes] = useState<boolean>(true);

  // Derived calculation: Waiver for OTS
  const waiverAmount = Math.max(0, totalOutstanding - otsAmount);
  const waiverPercent = totalOutstanding > 0 ? Math.round((waiverAmount / totalOutstanding) * 100) : 0;

  // Format dates for display
  const formatDateDisplay = (dateStr: string) => {
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  const formatMarathiDate = (dateStr: string) => {
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const day = parseInt(parts[2], 10);
        const monthIdx = parseInt(parts[1], 10) - 1;
        const year = parts[0];
        const marathiMonths = [
          'जानेवारी', 'फेब्रुवारी', 'मार्च', 'एप्रिल', 'मे', 'जून',
          'जुलै', 'ऑगस्ट', 'सप्टेंबर', 'ऑक्टोबर', 'नोव्हेंबर', 'डिसेंबर'
        ];
        if (monthIdx >= 0 && monthIdx < 12) {
          return `${day} ${marathiMonths[monthIdx]} ${year}`;
        }
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  const formatHindiDate = (dateStr: string) => {
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const day = parseInt(parts[2], 10);
        const monthIdx = parseInt(parts[1], 10) - 1;
        const year = parts[0];
        const hindiMonths = [
          'जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून',
          'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'
        ];
        if (monthIdx >= 0 && monthIdx < 12) {
          return `${day} ${hindiMonths[monthIdx]} ${year}`;
        }
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  // Helper to build replacement dictionary for tokens
  const tokenValues = useMemo(() => {
    const bank = bankNameCustom || account.bankName || 'Bank of Maharashtra';
    const branch = account.branchName || account.branchCode || 'Main Branch';
    const loanNo = isMultiAccount
      ? allLoanNumbers
      : account.loanNumber || account.accountId || 'N/A';
    const custName = account.customerName || 'Borrower';
    const agentName = account.assignedAgentName || currentUser.name || 'Recovery Executive';
    const agentPhone = currentUser.mobile || '+91 98220 11223';

    let formattedLokDate = formatDateDisplay(lokAdalatDate);
    let formattedDueDate = formatDateDisplay(paymentDueDate);
    let formattedValidTill = formatDateDisplay(otsValidTill);

    if (language === 'MR') {
      formattedLokDate = formatMarathiDate(lokAdalatDate);
      formattedDueDate = formatMarathiDate(paymentDueDate);
      formattedValidTill = formatMarathiDate(otsValidTill);
    } else if (language === 'HI') {
      formattedLokDate = formatHindiDate(lokAdalatDate);
      formattedDueDate = formatHindiDate(paymentDueDate);
      formattedValidTill = formatHindiDate(otsValidTill);
    }

    return {
      '{CUSTOMER_NAME}': custName,
      '{LOAN_NO}': loanNo,
      '{LOAN_TYPE}': productOf(account) || account.facility || 'Loan',
      '{OVERDUE_AMOUNT}': overdueAmount.toLocaleString('en-IN'),
      '{TOTAL_OUTSTANDING}': totalOutstanding.toLocaleString('en-IN'),
      '{OTS_AMOUNT}': otsAmount.toLocaleString('en-IN'),
      '{ACCOUNT_COUNT}': String(linked.length),
      '{ACCOUNT_BREAKDOWN}': perAccountBreakdown,
      '{WAIVER_AMOUNT}': waiverAmount.toLocaleString('en-IN'),
      '{WAIVER_PERCENT}': `${waiverPercent}`,
      '{DUE_DATE}': formattedDueDate,
      '{VALID_TILL_DATE}': formattedValidTill,
      '{LOK_ADALAT_DATE}': formattedLokDate,
      '{LOK_ADALAT_TIME}': lokAdalatTime,
      '{LOK_ADALAT_VENUE}': lokAdalatVenue,
      '{BRANCH_NAME}': branch,
      '{BANK_NAME}': bank,
      '{AGENT_NAME}': agentName,
      '{AGENT_PHONE}': agentPhone,
      '{PAYMENT_LINK}': paymentLink,
    };
  }, [
    account,
    currentUser,
    bankNameCustom,
    overdueAmount,
    totalOutstanding,
    otsAmount,
    waiverAmount,
    waiverPercent,
    paymentDueDate,
    otsValidTill,
    lokAdalatDate,
    lokAdalatTime,
    lokAdalatVenue,
    language,
    paymentLink,
  ]);

  // Builds the numbered payment options list. Entries that come back empty are
  // dropped and the rest are renumbered, so a blank payment link never leaves a
  // bare "2." with nothing after it in the message the customer receives.
  const numberedOptions = (options: (string | false | null | undefined)[]): string =>
    options
      .filter((opt): opt is string => typeof opt === 'string' && opt.trim().length > 0)
      .map((opt, i) => `${i + 1}. ${opt}`)
      .join('\n');

  // Master Template Generator Function
  const generateTemplate = (cat: WhatsAppCategory, lang: WhatsAppLanguage): string => {
    const v = tokenValues;

    if (cat === 'PAYMENT_REMINDER') {
      if (lang === 'MR') {
        return `सस्नेह नमस्कार, *${v['{CUSTOMER_NAME}']}*,

*${v['{BANK_NAME}']}* (${v['{BRANCH_NAME}']} शाखा) कडील आपल्या *${v['{LOAN_TYPE}']}* कर्ज खाते क्र. *${v['{LOAN_NO}']}* बाबत ही महत्त्वाची आठवण आहे.

• थकीत हप्ता रक्कम: *₹${v['{OVERDUE_AMOUNT}']}*
• एकूण येणे बाकी: *₹${v['{TOTAL_OUTSTANDING}']}*
• भरणा अंतिम तारीख: *${v['{DUE_DATE}']}*

आपला CIBIL स्कोअर चांगला राहण्यासाठी आणि अतिरिक्त दंड टाळण्यासाठी कृपया थकीत रक्कम तातडीने जमा करावी.

💳 भरणा पर्याय:
${numberedOptions([
  `${v['{BRANCH_NAME}']} शाखेत जाऊन`,
  `संपर्क - वसुली प्रतिनिधि - *${v['{AGENT_PHONE}']}*`,
])}

आपला नम्र,
कर्ज वसुली विभाग`;
      }
      if (lang === 'HI') {
        return `प्रिय *${v['{CUSTOMER_NAME}']}*,

*${v['{BANK_NAME}']}* (${v['{BRANCH_NAME}']} शाखा) में आपके *${v['{LOAN_TYPE}']}* ऋण खाता संख्या *${v['{LOAN_NO}']}* के संबंध में यह आवश्यक भुगतान सूचना है।

• कुल बकाया राशि: *₹${v['{OVERDUE_AMOUNT}']}*
• कुल शेष ऋण: *₹${v['{TOTAL_OUTSTANDING}']}*
• भुगतान अंतिम तिथि: *${v['{DUE_DATE}']}*

कृपया अपना CIBIL क्रेडिट स्कोर सुरक्षित रखने और अतिरिक्त पेनल्टी से बचने हेतु बकाया राशि तुरंत जमा करें।

💳 भुगतान विकल्प:
${numberedOptions([
  `${v['{BRANCH_NAME}']} बैंक शाखा में`,
  `संपर्क - वसुली प्रतिनिधि - *${v['{AGENT_PHONE}']}*`,
])}

सादर,
ऋण वसूली विभाग`;
      }
      // English
      return `Dear *${v['{CUSTOMER_NAME}']}*,

This is an official payment reminder regarding your *${v['{LOAN_TYPE}']}* Loan Account *${v['{LOAN_NO}']}* with *${v['{BANK_NAME}']}* (${v['{BRANCH_NAME}']} Branch).

• Pending Overdue Amount: *₹${v['{OVERDUE_AMOUNT}']}*
• Total Outstanding Balance: *₹${v['{TOTAL_OUTSTANDING}']}*
• Due Date for Payment: *${v['{DUE_DATE}']}*

Kindly clear the overdue installment immediately to maintain a healthy CIBIL credit score and prevent penalty interest.

💳 Payment Options:
${numberedOptions([
  `Visit ${v['{BRANCH_NAME}']} Branch`,
  `Contact - Recovery Representative - *${v['{AGENT_PHONE}']}*`,
])}

Regards,
Recovery Operations Desk`;
    }

    if (cat === 'LOK_ADALAT') {
      if (lang === 'MR') {
        return `📢 *लोक अदालतीमध्ये कर्ज खाते निकाली काढण्याची विशेष संधी*

*खातेधारकाचे नाव:* ${v['{CUSTOMER_NAME}']}

आपले *${v['{BANK_NAME}']}* मधील कर्ज खाते थकीत असून, सदर कर्ज खात्याबाबत *लोक अदालतीच्या माध्यमातून तडजोड करून प्रकरण निकाली काढण्याची संधी* उपलब्ध आहे.

आपल्या कर्ज खात्याबाबत उपलब्ध *तडजोडीची संधी जाणून घेण्यासाठी व पुढील कार्यवाहीसाठी* लोक अदालतीस उपस्थित राहावे.

📅 *दिनांक:* ${v['{LOK_ADALAT_DATE}']}
⏰ *वेळ:* ${v['{LOK_ADALAT_TIME}']}
📍 *स्थळ:* ${v['{LOK_ADALAT_VENUE}']}

आपल्या थकीत कर्ज खात्याचा *तडजोडीने निपटारा करण्यासाठी ही संधी गमावू नये.*

_सदर संदेश ${v['{BANK_NAME}']} मधील आपल्या कर्ज खात्याच्या संदर्भात पाठविण्यात येत आहे._`;
      }
      if (lang === 'HI') {
        return `📢 *लोक अदालत में ऋण खाता निपटारे का विशेष अवसर*

*खाताधारक का नाम:* ${v['{CUSTOMER_NAME}']}

आपका *${v['{BANK_NAME}']}* में ऋण खाता बकाया है, और इस ऋण खाते के संबंध में *लोक अदालत के माध्यम से समझौता कर प्रकरण के निस्तारण का अवसर* उपलब्ध है।

अपने ऋण खाते के संबंध में उपलब्ध *समझौते का अवसर जानने एवं अग्रिम कार्यवाही हेतु* लोक अदालत में उपस्थित रहें।

📅 *दिनांक:* ${v['{LOK_ADALAT_DATE}']}
⏰ *समय:* ${v['{LOK_ADALAT_TIME}']}
📍 *स्थान:* ${v['{LOK_ADALAT_VENUE}']}

अपने बकाया ऋण खाते का *पारस्परिक समझौते से स्थायी निपटारा करने हेतु यह अवसर न चूकें।*

_यह संदेश ${v['{BANK_NAME}']} में आपके ऋण खाते के संदर्भ में प्रेषित किया जा रहा है._`;
      }
      // English
      return `📢 *SPECIAL OPPORTUNITY TO SETTLE LOAN ACCOUNT IN LOK ADALAT*

*Account Holder Name:* ${v['{CUSTOMER_NAME}']}

Your loan account with *${v['{BANK_NAME}']}* is overdue, and a special opportunity is available to *amicably settle and close the matter through the upcoming Lok Adalat*.

You are requested to attend the Lok Adalat to *avail the settlement concession and proceed with account closure*.

📅 *Date:* ${v['{LOK_ADALAT_DATE}']}
⏰ *Time:* ${v['{LOK_ADALAT_TIME}']}
📍 *Venue:* ${v['{LOK_ADALAT_VENUE}']}

*Do not miss this golden opportunity to resolve and settle your overdue loan account.*

_This message is being sent in reference to your loan account with ${v['{BANK_NAME}']}_`;
    }

    if (cat === 'OTS_OFFER') {
      if (lang === 'MR') {
        return `🌟 *विशेष एकरकमी कर्ज तडजोड योजना (OTS) - मर्यादित मुदत ऑफर*

प्रति, *${v['{CUSTOMER_NAME}']}*,

विषय: कर्ज खाते क्र. *${v['{LOAN_NO}']}* (${v['{BANK_NAME}']}) साठी अंतिम OTS कर्जमुक्ती योजना.

आपणास कळविण्यात अत्यंत आनंद होत आहे की, बँक सक्षम प्राधिकाऱ्यांनी आपल्या कर्ज खात्यासाठी विशेष भरघोस सवलतीसह *OTS योजना* मंजूर केली आहे:

• मूळ एकूण येणे बाकी: *₹${v['{TOTAL_OUTSTANDING}']}*
• मंजूर एकरकमी तडजोड रक्कम: *₹${v['{OTS_AMOUNT}']}*
• एकूण मिळालेली थेट सवलत/माफी: *₹${v['{WAIVER_AMOUNT}']}* (${v['{WAIVER_PERCENT}']}% सूट)
• ⏳ *ऑफरची अंतिम मुदत:* *${v['{VALID_TILL_DATE}']}* पर्यंतच वैध!

सदर रक्कम *${v['{VALID_TILL_DATE}']}* पूर्वी भरल्यास:
१. संपूर्ण कर्जमुक्तीचा दाखला (NOC) तात्काळ प्रदान केला जाईल.
२. सर्व संभाव्य कायदेशीर / वसुली कारवाया पूर्णतः मागे घेण्यात येतील.
३. CIBIL व क्रेडिट रेकॉर्डवर कर्ज पूर्ण झाल्याची नोंद केली जाईल.

⚠️ *टीप:* मुदत संपल्यानंतर ही सवलत आपोआप रद्द होईल आणि मूळ संपूर्ण रक्कम ₹${v['{TOTAL_OUTSTANDING}']} कायदेशीर खर्चासह वसूल केली जाईल.

ऑफर स्वीकारण्यासाठी व खात्री करण्यासाठी त्वरित संपर्क साधा:
संपर्क - वसुली प्रतिनिधि - *${v['{AGENT_PHONE}']}*
${v['{BRANCH_NAME}']} शाखा`;
      }
      if (lang === 'HI') {
        return `🌟 *विशेष एकमुश्त समझौता योजना (OTS) - सीमित अवधि ऑफर*

सेवा में, *${v['{CUSTOMER_NAME}']}*,

विषय: ऋण खाता संख्या *${v['{LOAN_NO}']}* (${v['{BANK_NAME}']}) हेतु विशेष OTS समझौता।

बैंक प्रबंधन द्वारा आपके ऋण खाते के पूर्ण समाधान हेतु विशेष छूट के साथ एकमुश्त समझौता (OTS) स्वीकृत किया गया है:

• मूल कुल शेष ऋण: *₹${v['{TOTAL_OUTSTANDING}']}*
• स्वीकृत एकमुश्त निपटान राशि: *₹${v['{OTS_AMOUNT}']}*
• कुल प्राप्त छूट / ब्याज माफी: *₹${v['{WAIVER_AMOUNT}']}* (${v['{WAIVER_PERCENT}']}% छूट)
• ⏳ *ऑफर की वैधता:* केवल *${v['{VALID_TILL_DATE}']}* तक!

नियत तिथि से पूर्व उक्त राशि का भुगतान करने पर पूर्ण बेबाकी प्रमाणपत्र (NOC) जारी किया जाएगा तथा सभी कानूनी कार्यवाहियां निरस्त की जाएंगी।

⚠️ *चेतावनी:* नियत तिथि के पश्चात यह छूट स्वतः समाप्त हो जाएगी और संपूर्ण मूल बकाया राशि ₹${v['{TOTAL_OUTSTANDING}']} कानूनी व्यय सहित देय होगी।

ऑफर स्वीकार करने हेतु तत्काल संपर्क करें:
संपर्क - वसुली प्रतिनिधि - *${v['{AGENT_PHONE}']}*
${v['{BRANCH_NAME}']} शाखा`;
      }
      // English
      return `🌟 *SPECIAL ONE TIME SETTLEMENT (OTS) OFFER - LIMITED PERIOD ONLY*

Dear *${v['{CUSTOMER_NAME}']}*,

Subject: Final OTS Settlement for Loan Account *${v['{LOAN_NO}']}* (${v['{BANK_NAME}']})

We are pleased to inform you that the Bank Competent Authority has sanctioned a *Special One Time Settlement (OTS) Scheme* for your account with unprecedented waiver benefits:

• Original Outstanding Balance: *₹${v['{TOTAL_OUTSTANDING}']}*${
  Number(v['{ACCOUNT_COUNT}']) > 1
    ? `  (across ${v['{ACCOUNT_COUNT}']} loan accounts)\n${v['{ACCOUNT_BREAKDOWN}']}`
    : ''
}
• Approved One-Time Settlement Amount: *₹${v['{OTS_AMOUNT}']}*
• Total Direct Waiver / Concession: *₹${v['{WAIVER_AMOUNT}']}* (${v['{WAIVER_PERCENT}']}% Waiver)
• ⏳ *Strict Offer Validity:* Valid till *${v['{VALID_TILL_DATE}']}* only!

Upon payment of ₹${v['{OTS_AMOUNT}']} on or before ${v['{VALID_TILL_DATE}']}:
1. 100% Full & Final Loan Closure Certificate (NOC) will be issued.
2. All legal / recovery proceedings will be permanently dropped.
3. Account closure update will be submitted to Credit Bureaus (CIBIL).

⚠️ *Note:* If payment is not completed by ${v['{VALID_TILL_DATE}']}, this OTS concession stands revoked, and the full outstanding balance of ₹${v['{TOTAL_OUTSTANDING}']} with legal charges will be enforced.

To accept and lock this OTS offer, immediately contact:
Contact - Recovery Representative - *${v['{AGENT_PHONE}']}*
Branch: *${v['{BRANCH_NAME}']}*`;
    }

    if (cat === 'BROKEN_PTP') {
      if (lang === 'MR') {
        return `⚠️ *तातडीची सूचना: वचनानुसार हप्ता जमा न केल्याबाबत*

प्रिय *${v['{CUSTOMER_NAME}']}*,

आपल्या कर्ज खाते क्र. *${v['{LOAN_NO}']}* (${v['{BANK_NAME}']}) बाबत आपण दिलेली *₹${v['{OVERDUE_AMOUNT}']}* ची वचन तारीख टळलेली असून अद्याप खात्यावर भरणा झालेला नाही.

पुढील २४ तासांत थकीत रक्कम जमा न झाल्यास:
१. CIBIL क्रेडिट ब्युरोला त्वरित डिफॉल्टर नोंद पाठवली जाईल.
२. प्रत्यक्ष घर / कार्यालय तपासणी व वसुली भेट आयोजित केली जाईल.
३. पुढील कायदेशीर नोटीस व न्यायालयात वसुली कारवाई सुरू केली जाईल.

कृपया त्वरित भरणा करा किंवा थेट संपर्क साधा:
संपर्क - वसुली प्रतिनिधि - *${v['{AGENT_PHONE}']}*`;
      }
      if (lang === 'HI') {
        return `⚠️ *अति आवश्यक: वादा अनुसार भुगतान न होने की सूचना*

प्रिय *${v['{CUSTOMER_NAME}']}*,

ऋण खाता संख्या *${v['{LOAN_NO}']}* (${v['{BANK_NAME}']}) के लिए आपके द्वारा दी गई तारीख पर *₹${v['{OVERDUE_AMOUNT}']}* का भुगतान प्राप्त नहीं हुआ है।

अंतिम अवसर: कृपया आगामी 24 घंटे के भीतर बकाया राशि जमा करें अन्यथा कानूनी प्रक्रिया एवं CIBIL डिफॉल्ट रिपोर्टिंग प्रारंभ की जाएगी।

संपर्क करें:
संपर्क - वसुली प्रतिनिधि - *${v['{AGENT_PHONE}']}*`;
      }
      // English
      return `⚠️ *URGENT: MISSED PROMISE-TO-PAY (PTP) NOTICE*

Dear *${v['{CUSTOMER_NAME}']}*,

As per our records for Loan Account *${v['{LOAN_NO}']}* (${v['{BANK_NAME}']}), your agreed payment commitment of *₹${v['{OVERDUE_AMOUNT}']}* was not received on the scheduled date.

Failure to clear the overdue amount within the next *24 hours* will result in:
1. Immediate adverse default reporting to Credit Bureaus (CIBIL / Experian)
2. Scheduling of an urgent physical on-site recovery inspection at your registered address
3. Initiation of formal legal recovery proceedings

Please pay immediately at the Branch or contact us to resolve this urgently.

Contact - Recovery Representative - *${v['{AGENT_PHONE}']}*

Recovery Operations`;
    }

    if (cat === 'FIELD_VISIT') {
      if (lang === 'MR') {
        return `📍 *अधिकृत क्षेत्रीय तपासणी व वसुली भेट सूचना*

प्रति, *${v['{CUSTOMER_NAME}']}*,

कर्ज खाते क्र.: *${v['{LOAN_NO}']}* | थकीत रक्कम: *₹${v['{OVERDUE_AMOUNT}']}*

आपणास कळविण्यात येते की, वारंवार सूचना देऊनही थकीत भरणा न झाल्यामुळे, *${v['{BANK_NAME}']}* चे अधिकृत वसुली प्रतिनिधि आपल्या नोंदणीकृत पत्त्यावर प्रत्यक्ष भेटीसाठी येत आहेत.

भेट नियोजित तारीख: *${v['{DUE_DATE}']}*

कृपया प्रत्यक्ष भेटीदरम्यान उपस्थित राहून आपली थकीत रक्कम अथवा तडजोड कागदपत्रे तयार ठेवावीत.
संपर्क - वसुली प्रतिनिधि - *${v['{AGENT_PHONE}']}*`;
      }
      if (lang === 'HI') {
        return `📍 *आधिकारिक फील्ड विजिट एवं भौतिक सत्यापन सूचना*

सेवा में, *${v['{CUSTOMER_NAME}']}*,

ऋण खाता संख्या: *${v['{LOAN_NO}']}* | बकाया राशि: *₹${v['{OVERDUE_AMOUNT}']}*

आपको सूचित किया जाता है कि बकाया भुगतान न होने के कारण, *${v['{BANK_NAME}']}* के अधिकृत वसुली प्रतिनिधि आपके पंजीकृत पते पर भौतिक सत्यापन एवं वसूली हेतु आ रहे हैं।

भ्रमण तिथि: *${v['{DUE_DATE}']}*

कृपया उपस्थिति सुनिश्चित कर सहयोग करें।
संपर्क - वसुली प्रतिनिधि - *${v['{AGENT_PHONE}']}*`;
      }
      // English
      return `📍 *OFFICIAL FIELD VISIT & ASSET INSPECTION NOTICE*

Dear *${v['{CUSTOMER_NAME}']}*,

Loan Account: *${v['{LOAN_NO}']}* | Overdue Amount: *₹${v['{OVERDUE_AMOUNT}']}*

Due to non-clearance of your pending loan dues despite prior intimations, our authorized Recovery Representative has been scheduled to visit your registered residence / business premises.

Scheduled Visit Date: *${v['{DUE_DATE}']}*

Please ensure availability with repayment mode to prevent escalation.
Contact - Recovery Representative - *${v['{AGENT_PHONE}']}*
Recovery Inspection Cell`;
    }

    return '';
  };

  // When category, language, or parameters change in auto mode, update messageText
  useEffect(() => {
    if (draftMode === 'auto') {
      const generated = generateTemplate(category, language);
      setMessageText(generated);
    }
  }, [
    category,
    language,
    draftMode,
    otsAmount,
    otsValidTill,
    lokAdalatDate,
    lokAdalatVenue,
    paymentDueDate,
    paymentLink,
    tokenValues,
  ]);

  // Insert token chip in manual mode
  const handleInsertToken = (token: string) => {
    setMessageText((prev) => prev + ` ${token} `);
  };

  if (!isOpen) return null;

  // Resolve target mobile number
  let targetMobile = account.mobile || '';
  if (recipientType === 'alternate') {
    targetMobile = account.alternateMobile || account.mobile || '';
  } else if (recipientType === 'coordinator') {
    targetMobile = '+919822011223';
  } else if (recipientType === 'agent') {
    targetMobile = currentUser.mobile || '+919822011223';
  } else if (recipientType === 'custom') {
    targetMobile = customMobile;
  }

  const cleanMobile = targetMobile.replace(/[^0-9]/g, '');
  const waDeepLink = cleanMobile
    ? `https://api.whatsapp.com/send?phone=${cleanMobile.length === 10 ? '91' + cleanMobile : cleanMobile}&text=${encodeURIComponent(messageText)}`
    : `https://api.whatsapp.com/send?text=${encodeURIComponent(messageText)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(messageText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = messageText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleOpenWhatsApp = () => {
    if (logToNotes) {
      const catLabel =
        category === 'PAYMENT_REMINDER'
          ? 'Payment Reminder'
          : category === 'LOK_ADALAT'
          ? 'Lok Adalat Special Notice'
          : category === 'OTS_OFFER'
          ? 'Limited Period OTS Offer'
          : category === 'BROKEN_PTP'
          ? 'Broken PTP Alert'
          : 'Field Visit Notice';

      addAgentNote(
        account.accountId,
        `[WhatsApp Draft Dispatched - ${catLabel}]: Message (${language}) opened in WhatsApp for ${account.customerName} (${targetMobile}). Overdue: ₹${overdueAmount.toLocaleString('en-IN')}, OTS: ₹${otsAmount.toLocaleString('en-IN')}. Logged by ${currentUser.name}.`,
        'Follow Up'
      );

      if (category === 'OTS_OFFER') {
        logWhatsAppOffer({
          accountId: account.accountId,
          customerName: account.customerName,
          customerMobile: targetMobile,
          finalOTSAmount: otsAmount,
          userName: currentUser.name || 'Recovery Executive',
          userMobile: currentUser.mobile || '',
          // These five were missing, so every entry was rejected and no log
          // of the offer was kept at all.
          userId: currentUser.agentId || currentUser.id,
          totalOutstanding,
          messageType: 'WhatsApp OTS Offer',
          messagePreview: messageText,
          status: 'Dispatched via WhatsApp Link',
        });
      }
    }

    window.open(waDeepLink, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <div
      id="whatsapp-draft-modal-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto font-sans animate-in fade-in duration-150"
    >
      <div
        id="whatsapp-draft-modal-container"
        className="bg-white border border-slate-200 rounded-2xl w-full max-w-4xl shadow-2xl text-slate-800 flex flex-col max-h-[92vh] overflow-hidden my-auto"
      >
        {/* Header */}
        <div className="px-5 py-3.5 bg-emerald-600 text-white flex items-center justify-between shadow-xs">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-emerald-700/80 rounded-xl text-white shadow-xs">
              <MessageSquare className="w-5 h-5" />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-base tracking-tight">WhatsApp Notification Center &amp; Smart Draft Studio</h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/50 text-white uppercase tracking-wider border border-emerald-400/50">
                  {draftMode === 'auto' ? '⚡ Auto-Generated' : '✍️ Manual Custom Draft'}
                </span>
              </div>
              <p className="text-xs text-emerald-100 flex items-center gap-2 mt-0.5">
                <span>Account: <strong className="font-mono text-white">{account.loanNumber || account.accountId}</strong></span>
                <span>•</span>
                <span>Customer: <strong className="text-white">{account.customerName}</strong></span>
                <span>•</span>
                <span>Overdue: <strong className="text-white">₹{overdueAmount.toLocaleString('en-IN')}</strong></span>
              </p>
            </div>
          </div>
          <button
            id="close-whatsapp-draft-modal-btn"
            onClick={onClose}
            className="p-1.5 text-emerald-100 hover:text-white hover:bg-emerald-700/50 rounded-lg cursor-pointer transition"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode Selector & Category Navigation */}
        <div className="bg-slate-50 border-b border-slate-200 px-5 py-2.5 flex flex-wrap items-center justify-between gap-3">
          {/* Draft Mode Toggle (Auto vs Manual) */}
          <div className="flex items-center bg-white p-0.5 rounded-xl border border-slate-200 shadow-2xs">
            <button
              id="draft-mode-auto-btn"
              type="button"
              onClick={() => setDraftMode('auto')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition cursor-pointer ${
                draftMode === 'auto'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>Auto-Generated (Smart)</span>
            </button>
            <button
              id="draft-mode-manual-btn"
              type="button"
              onClick={() => setDraftMode('manual')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition cursor-pointer ${
                draftMode === 'manual'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Manual Custom Draft</span>
            </button>
          </div>

          {/* Language Selector - Marathi set as primary default */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-slate-500 mr-1">भाषा / Language:</span>
            {[
              { code: 'MR', label: '🚩 मराठी (Default)' },
              { code: 'EN', label: 'English' },
              { code: 'HI', label: 'हिंदी' },
            ].map((l) => (
              <button
                key={l.code}
                id={`lang-btn-${l.code.toLowerCase()}`}
                type="button"
                onClick={() => setLanguage(l.code as WhatsAppLanguage)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition cursor-pointer ${
                  language === l.code
                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs ring-2 ring-emerald-300/60'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Suggestion Categories Bar */}
        <div className="bg-slate-100/70 border-b border-slate-200 px-5 py-2 flex items-center gap-2 overflow-x-auto scrollbar-none text-xs">
          <span className="text-slate-500 font-bold uppercase text-[10px] tracking-wider shrink-0 flex items-center gap-1">
            <Tag className="w-3 h-3 text-emerald-600" />
            सूचना प्रकार:
          </span>

          {[
            { id: 'PAYMENT_REMINDER', label: '⏰ हप्ता स्मरण (Payment Reminder)', desc: 'Overdue & EMI due reminder' },
            { id: 'LOK_ADALAT', label: '⚖️ लोक अदालत नोटीस (Lok Adalat)', desc: 'Pre-litigation settlement & waiver' },
            { id: 'OTS_OFFER', label: '🌟 एकरकमी कर्ज तडजोड (OTS Offer)', desc: 'Special discount & NOC terms' },
            { id: 'BROKEN_PTP', label: '⚠️ वचनभंग सूचना (Broken PTP)', desc: 'Urgent 24-hr payment notice' },
            { id: 'FIELD_VISIT', label: '📍 प्रत्यक्ष वसुली भेट (Field Visit)', desc: 'Prior visit inspection intimation' },
          ].map((cat) => (
            <button
              key={cat.id}
              id={`cat-suggestion-${cat.id.toLowerCase()}`}
              type="button"
              onClick={() => {
                setCategory(cat.id as WhatsAppCategory);
                if (draftMode === 'manual') {
                  // If switching in manual mode, offer to load template
                  const templ = generateTemplate(cat.id as WhatsAppCategory, language);
                  setMessageText(templ);
                }
              }}
              className={`px-3 py-1.5 rounded-xl font-bold whitespace-nowrap border transition cursor-pointer flex items-center gap-1.5 shadow-2xs ${
                category === cat.id
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs ring-2 ring-emerald-300/60'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
              }`}
            >
              <span>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Main Content Body */}
        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 lg:grid-cols-12 gap-5 text-xs">
          {/* Left Column: Controls, Recipient, and Parameters (5 cols) */}
          <div className="lg:col-span-5 space-y-4">
            {/* Recipient Selector */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2.5">
              <label className="block text-slate-800 font-bold flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-emerald-600" />
                  Target Recipient
                </span>
                <span className="text-[11px] text-slate-500 font-mono font-normal">
                  Send to: <strong className="text-emerald-700 font-bold">{targetMobile || 'No number'}</strong>
                </span>
              </label>

              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => setRecipientType('customer')}
                  className={`p-2 rounded-lg text-left border transition cursor-pointer ${
                    recipientType === 'customer'
                      ? 'bg-emerald-50 border-emerald-400 text-emerald-900 font-bold'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="block text-[10px] text-slate-500 uppercase">Borrower Mobile</span>
                  <span className="truncate block font-mono text-[11px]">{account.mobile || 'None'}</span>
                </button>

                {account.alternateMobile ? (
                  <button
                    type="button"
                    onClick={() => setRecipientType('alternate')}
                    className={`p-2 rounded-lg text-left border transition cursor-pointer ${
                      recipientType === 'alternate'
                        ? 'bg-emerald-50 border-emerald-400 text-emerald-900 font-bold'
                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <span className="block text-[10px] text-slate-500 uppercase">Guarantor / Alt</span>
                    <span className="truncate block font-mono text-[11px]">{account.alternateMobile}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setRecipientType('agent')}
                    className={`p-2 rounded-lg text-left border transition cursor-pointer ${
                      recipientType === 'agent'
                        ? 'bg-emerald-50 border-emerald-400 text-emerald-900 font-bold'
                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <span className="block text-[10px] text-slate-500 uppercase">Agent Copy</span>
                    <span className="truncate block font-mono text-[11px]">{currentUser.mobile || 'Self'}</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setRecipientType('coordinator')}
                  className={`p-2 rounded-lg text-left border transition cursor-pointer ${
                    recipientType === 'coordinator'
                      ? 'bg-emerald-50 border-emerald-400 text-emerald-900 font-bold'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="block text-[10px] text-slate-500 uppercase">Coordinator Desk</span>
                  <span className="truncate block font-mono text-[11px]">+91 98220 11223</span>
                </button>

                <button
                  type="button"
                  onClick={() => setRecipientType('custom')}
                  className={`p-2 rounded-lg text-left border transition cursor-pointer ${
                    recipientType === 'custom'
                      ? 'bg-emerald-50 border-emerald-400 text-emerald-900 font-bold'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="block text-[10px] text-slate-500 uppercase">Custom Number</span>
                  <span className="truncate block font-mono text-[11px]">{customMobile || 'Enter below'}</span>
                </button>
              </div>

              {recipientType === 'custom' && (
                <div className="pt-1">
                  <input
                    type="tel"
                    value={customMobile}
                    onChange={(e) => setCustomMobile(e.target.value)}
                    placeholder="Enter 10-digit WhatsApp number (e.g. 9822123456)"
                    className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              )}
            </div>

            {/* Dynamic Parameter Settings tailored to the Category */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-3">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <span className="font-bold text-slate-800 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-emerald-600" />
                  Template Variables &amp; Parameters
                </span>
                <span className="text-[10px] text-slate-500">Live calculation</span>
              </div>

              {/* OTS Parameters */}
              {category === 'OTS_OFFER' && (
                <div className="space-y-2.5">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      Approved OTS Settlement Amount (₹):
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-slate-400 font-bold">₹</span>
                      <AmountInput
                        value={otsAmount}
                        onValueChange={setOtsAmount}
                        className="w-full bg-white border border-slate-300 rounded-lg pl-7 pr-3 py-1.5 font-mono text-xs font-bold text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div className="flex justify-between items-center text-[10px] text-slate-500 mt-1">
                      <span>Original: ₹{totalOutstanding.toLocaleString('en-IN')}</span>
                      <span className="font-bold text-emerald-600">Waiver: ₹{waiverAmount.toLocaleString('en-IN')} ({waiverPercent}%)</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      ⏳ OTS Offer Valid Till Date:
                    </label>
                    <input
                      type="date"
                      value={otsValidTill}
                      onChange={(e) => setOtsValidTill(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              )}

              {/* Lok Adalat Parameters */}
              {category === 'LOK_ADALAT' && (
                <div className="space-y-3 bg-white/70 p-3 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-800 text-[11px] flex items-center gap-1.5">
                      ⚖️ Lok Adalat Notice Parameters
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setBankNameCustom('Bank of Maharashtra');
                        setLokAdalatDate('2026-09-12');
                        setLokAdalatTime('सकाळी 11:00 वाजता');
                        setLokAdalatVenue('जिल्हा व सत्र न्यायालय परिसर, नवीन इमारत,\nछत्रपती संभाजीनगर – 431001, महाराष्ट्र.');
                      }}
                      className="text-[10px] text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded font-semibold border border-emerald-200 transition cursor-pointer"
                      title="Reset to 12 Sep 2026 Chhatrapati Sambhajinagar Notice"
                    >
                      Reset Sambhajinagar Defaults
                    </button>
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      🏛️ Bank / Financial Institution:
                    </label>
                    <input
                      type="text"
                      value={bankNameCustom}
                      onChange={(e) => setBankNameCustom(e.target.value)}
                      placeholder="Bank of Maharashtra"
                      className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-slate-700 font-semibold mb-1">
                        📅 Hearing Date:
                      </label>
                      <input
                        type="date"
                        value={lokAdalatDate}
                        onChange={(e) => setLokAdalatDate(e.target.value)}
                        className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <span className="text-[10px] text-slate-500 mt-0.5 block font-semibold">
                        {language === 'MR' ? formatMarathiDate(lokAdalatDate) : formatDateDisplay(lokAdalatDate)}
                      </span>
                    </div>

                    <div>
                      <label className="block text-slate-700 font-semibold mb-1">
                        ⏰ Hearing Time:
                      </label>
                      <input
                        type="text"
                        value={lokAdalatTime}
                        onChange={(e) => setLokAdalatTime(e.target.value)}
                        placeholder="सकाळी 11:00 वाजता"
                        className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      📍 Venue / Court Address:
                    </label>
                    <textarea
                      rows={2}
                      value={lokAdalatVenue}
                      onChange={(e) => setLokAdalatVenue(e.target.value)}
                      placeholder="जिल्हा व सत्र न्यायालय परिसर..."
                      className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    />
                  </div>
                </div>
              )}

              {/* Payment Reminder / Broken PTP / Field Visit Parameters */}
              {(category === 'PAYMENT_REMINDER' || category === 'BROKEN_PTP' || category === 'FIELD_VISIT') && (
                <div className="space-y-2.5">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      {category === 'FIELD_VISIT' ? 'Scheduled Visit Date:' : 'Payment Due Date:'}
                    </label>
                    <input
                      type="date"
                      value={paymentDueDate}
                      onChange={(e) => setPaymentDueDate(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      Payment Link or UPI / Branch Reference:
                    </label>
                    <input
                      type="text"
                      value={paymentLink}
                      onChange={(e) => setPaymentLink(e.target.value)}
                      placeholder="Leave blank to omit this line"
                      className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              )}

              {/* Action Log Checkbox */}
              <div className="pt-2 border-t border-slate-200">
                <label className="flex items-center gap-2 text-slate-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={logToNotes}
                    onChange={(e) => setLogToNotes(e.target.checked)}
                    className="rounded text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="font-semibold text-[11px]">Auto-log activity to Account History &amp; Agent Notes</span>
                </label>
              </div>
            </div>

            {/* Quick Variable Tag Chips in Manual Mode */}
            {draftMode === 'manual' && (
              <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-3 space-y-2">
                <span className="font-bold text-amber-900 text-[11px] block">
                  Click to Insert Smart Tags into Text:
                </span>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                  {[
                    '{CUSTOMER_NAME}',
                    '{BANK_NAME}',
                    '{LOAN_NO}',
                    '{OVERDUE_AMOUNT}',
                    '{TOTAL_OUTSTANDING}',
                    '{OTS_AMOUNT}',
                    '{VALID_TILL_DATE}',
                    '{LOK_ADALAT_DATE}',
                    '{LOK_ADALAT_TIME}',
                    '{LOK_ADALAT_VENUE}',
                    '{DUE_DATE}',
                    '{AGENT_NAME}',
                    '{AGENT_PHONE}',
                    '{BRANCH_NAME}',
                    '{PAYMENT_LINK}',
                  ].map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => handleInsertToken(tag)}
                      className="px-2 py-0.5 bg-white hover:bg-amber-100 text-amber-900 border border-amber-300 rounded-md font-mono text-[10px] font-semibold transition cursor-pointer"
                    >
                      +{tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Editor & WhatsApp Realistic Chat Preview (7 cols) */}
          <div className="lg:col-span-7 flex flex-col space-y-3">
            {/* Editor Area */}
            <div className="space-y-1.5 flex-1 flex flex-col">
              <div className="flex items-center justify-between">
                <label className="text-slate-800 font-bold flex items-center gap-1.5">
                  <Edit3 className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Message Body</span>
                  {draftMode === 'auto' ? (
                    <span className="text-[10px] text-emerald-600 font-normal">(Auto-calculated)</span>
                  ) : (
                    <span className="text-[10px] text-blue-600 font-normal">(Custom editable)</span>
                  )}
                </label>
                <div className="flex items-center gap-3 text-[11px] text-slate-500">
                  <span>{messageText.length} chars</span>
                  <span>•</span>
                  <span>{messageText.trim().split(/\s+/).filter(Boolean).length} words</span>
                  <button
                    type="button"
                    onClick={() => {
                      const fresh = generateTemplate(category, language);
                      setMessageText(fresh);
                    }}
                    className="text-emerald-700 hover:text-emerald-900 flex items-center gap-1 font-semibold cursor-pointer"
                    title="Reset to freshly generated template"
                  >
                    <RefreshCw className="w-3 h-3" />
                    <span>Regenerate</span>
                  </button>
                </div>
              </div>

              <textarea
                id="whatsapp-message-textarea"
                rows={9}
                value={messageText}
                onChange={(e) => {
                  setMessageText(e.target.value);
                  if (draftMode === 'auto') {
                    setDraftMode('manual');
                  }
                }}
                placeholder="Type or customize your WhatsApp message here..."
                className="w-full flex-1 bg-slate-50 border border-slate-300 rounded-xl p-3.5 font-mono text-[11.5px] leading-relaxed text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-2xs resize-none"
              />
            </div>

            {/* Realistic WhatsApp Chat Bubble Preview */}
            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs bg-[#EFEAE2]">
              {/* WhatsApp Header bar */}
              <div className="bg-[#075E54] text-white px-3.5 py-2 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-xs">
                    {account.customerName ? account.customerName.charAt(0).toUpperCase() : 'B'}
                  </div>
                  <div>
                    <div className="font-bold text-[11px] truncate max-w-[200px]">{account.customerName}</div>
                    <div className="text-[9px] text-emerald-200 font-mono">
                      {targetMobile || 'No mobile set'} • online
                    </div>
                  </div>
                </div>
                <span className="text-[10px] text-emerald-100 uppercase tracking-wider font-semibold">
                  WhatsApp Preview
                </span>
              </div>

              {/* Chat Canvas with WhatsApp styled balloon */}
              <div className="p-3.5 max-h-48 overflow-y-auto flex flex-col items-end">
                <div className="bg-[#DCF8C6] text-slate-900 p-3 rounded-2xl rounded-tr-xs shadow-xs max-w-[92%] text-[11px] leading-relaxed font-sans whitespace-pre-wrap border border-[#c4e6ac]">
                  {messageText || (
                    <span className="text-slate-400 italic">No message drafted yet.</span>
                  )}
                  <div className="flex items-center justify-end gap-1 text-[9px] text-slate-500 mt-1">
                    <span>Just now</span>
                    <span className="text-blue-500 font-bold">✓✓</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-5 py-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="font-semibold text-slate-800">Target Destination:</span>
            <span className="font-mono bg-white px-2 py-0.5 rounded border border-slate-300 font-bold text-emerald-700">
              {targetMobile || 'Please provide mobile number'}
            </span>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              id="copy-whatsapp-text-btn"
              type="button"
              onClick={handleCopy}
              className="px-3.5 py-2 bg-white hover:bg-slate-100 text-slate-700 rounded-xl border border-slate-300 font-bold text-xs flex items-center gap-1.5 transition cursor-pointer shadow-2xs"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-slate-500" />}
              <span>{copied ? 'Copied to Clipboard!' : 'Copy Text'}</span>
            </button>

            <button
              id="cancel-whatsapp-btn"
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-medium text-xs transition cursor-pointer"
            >
              Cancel
            </button>

            <button
              id="send-whatsapp-btn"
              type="button"
              onClick={handleOpenWhatsApp}
              className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-sm flex items-center gap-2 text-xs transition cursor-pointer"
            >
              <Send className="w-4 h-4" />
              <span>Send via WhatsApp</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
